import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthProvider";
import {
  addPostComment,
  archivePost,
  createPost,
  deletePost,
  deletePostComment,
  friendlyMessage,
  getPost,
  listPostComments,
  listPosts,
  publishPost,
  updatePost,
} from "./projectApi";
import type {
  AddPostCommentRequest,
  CreatePostRequest,
  Post,
  PostComment,
  UpdatePostRequest,
} from "../models/project";

/** Which nested screen the blog section is currently showing. */
export type BlogScreen = "feed" | "editor" | "post";

// Orchestration + data for a project's blog (Flow-style feed): the post list, a new/edit post
// editor, and the currently-selected post with its threaded comments/replies. Scoped to a single
// projectId.
export function useBlog(projectId: string | null) {
  const { token } = useAuth();
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const getToken = useMemo(() => () => tokenRef.current(), []);

  const [screen, setScreen] = useState<BlogScreen>("feed");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [posts, setPosts] = useState<Post[]>([]);
  const [editingPostId, setEditingPostId] = useState<string | null>(null);

  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [comments, setComments] = useState<PostComment[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const reload = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      setPosts(await listPosts(getToken, projectId));
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setLoading(false);
    }
  }, [getToken, projectId]);

  useEffect(() => {
    if (projectId) void reload();
  }, [projectId, reload]);

  function openEditor(postId: string | null) {
    setEditingPostId(postId);
    setScreen("editor");
  }

  function backToFeed() {
    setScreen("feed");
    setEditingPostId(null);
  }

  async function addPost(req: CreatePostRequest): Promise<Post | null> {
    if (!projectId) return null;
    setError(null);
    try {
      const created = await createPost(getToken, projectId, req);
      await reload();
      return created;
    } catch (e) {
      setError(friendlyMessage(e));
      return null;
    }
  }

  async function savePost(id: string, req: UpdatePostRequest): Promise<Post | null> {
    setError(null);
    try {
      const updated = await updatePost(getToken, id, req);
      setPosts((cur) => cur.map((p) => (p.id === id ? updated : p)));
      setSelectedPost((cur) => (cur && cur.id === id ? updated : cur));
      return updated;
    } catch (e) {
      setError(friendlyMessage(e));
      return null;
    }
  }

  async function publish(id: string) {
    setError(null);
    try {
      const updated = await publishPost(getToken, id);
      setPosts((cur) => cur.map((p) => (p.id === id ? updated : p)));
      setSelectedPost((cur) => (cur && cur.id === id ? updated : cur));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function archive(id: string) {
    setError(null);
    try {
      const updated = await archivePost(getToken, id);
      setPosts((cur) => cur.map((p) => (p.id === id ? updated : p)));
      setSelectedPost((cur) => (cur && cur.id === id ? updated : cur));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function removePost(id: string) {
    setError(null);
    try {
      await deletePost(getToken, id);
      setPosts((cur) => cur.filter((p) => p.id !== id));
      if (selectedPostId === id) backToFeed2();
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  const loadDetail = useCallback(
    async (id: string) => {
      setLoadingDetail(true);
      setError(null);
      try {
        const [post, cmts] = await Promise.all([getPost(getToken, id), listPostComments(getToken, id)]);
        setSelectedPost(post);
        setComments(cmts);
      } catch (e) {
        setError(friendlyMessage(e));
      } finally {
        setLoadingDetail(false);
      }
    },
    [getToken],
  );

  function openPost(id: string) {
    setSelectedPostId(id);
    setScreen("post");
    void loadDetail(id);
  }

  function backToFeed2() {
    setScreen("feed");
    setSelectedPostId(null);
    setSelectedPost(null);
    setComments([]);
  }

  async function addComment(req: AddPostCommentRequest) {
    if (!selectedPostId) return;
    setError(null);
    try {
      const cmt = await addPostComment(getToken, selectedPostId, req);
      setComments((cur) => [...cur, cmt]);
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function removeComment(commentId: string) {
    setError(null);
    try {
      await deletePostComment(getToken, commentId);
      setComments((cur) => cur.filter((c) => c.id !== commentId && c.parentCommentId !== commentId));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  return {
    getToken,
    screen,
    setScreen,
    error,
    clearError: () => setError(null),
    loading,
    posts,
    reload,
    editingPostId,
    openEditor,
    backToFeed,
    addPost,
    savePost,
    publish,
    archive,
    removePost,

    selectedPostId,
    selectedPost,
    comments,
    loadingDetail,
    openPost,
    backToPostFeed: backToFeed2,
    addComment,
    removeComment,
  };
}
